import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6189l3n6d2i4h4oa5q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-[#1E3A3A] to-[#0F2626] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 800">
          <path
            d="M100,400 Q300,200 500,400 T900,400"
            stroke="white"
            strokeWidth="1"
            fill="none"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-16">
          <div>
            <div className="mb-8">
              <h2 className="text-white text-4xl mb-6" style={{ fontFamily: 'serif' }}>
                <a href="#contact" className="hover:text-[#14B8A6] transition-colors cursor-pointer">
                  今すぐ無料相談・お見積もり
                </a>
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              <div>
                <label htmlFor="company" className="block text-white text-sm mb-2">
                  会社名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#14B8A6] transition-colors"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-white text-sm mb-2">
                  お名前 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#14B8A6] transition-colors"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white text-sm mb-2">
                  メールアドレス <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#14B8A6] transition-colors"
                  placeholder="example@company.co.jp"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-white text-sm mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#14B8A6] transition-colors"
                  placeholder="03-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white text-sm mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#14B8A6] transition-colors resize-none"
                  placeholder="ご質問やご要望をお聞かせください"
                />
                <div className="text-white/50 text-xs mt-1 text-right">
                  {formData.message.length}/500文字
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#14B8A6] text-white py-4 rounded-lg font-medium hover:bg-[#0F9B8E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500/50 text-white px-4 py-3 rounded-lg text-sm">
                  お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}
            </form>
          </div>

          <div className="grid grid-cols-2 gap-12">
            <div>
              <h3 className="text-white font-medium mb-4">サービス</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                    料金プラン
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('flow')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                    導入の流れ
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                    よくある質問
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-medium mb-4">サポート</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                    よくある質問
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => document.getElementById('flow')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                    導入の流れ
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-medium mb-4">会社情報</h3>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm">
                    会社概要
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm">
                    お問い合わせ
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-white/50 text-sm">
            © 2025 合同会社ふらっと
          </div>

          <div className="flex items-center gap-6">
            <a href="#" className="text-white/50 hover:text-white text-sm transition-colors">
              プライバシーポリシー
            </a>
            <a href="https://readdy.ai/?ref=logo" className="text-white/50 hover:text-white text-sm transition-colors">
              Website Builder
            </a>
          </div>
        </div>

        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-[#14B8A6] text-white rounded-full shadow-lg hover:bg-[#0F9B8E] transition-all hover:scale-110 flex items-center justify-center cursor-pointer z-50"
        >
          <i className="ri-arrow-up-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}
